/**
 * ProvincialDashboard - Province-based delegate management
 * Shows province grid with delegate counts, upload capability, and filtering
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { getToken } from '@/lib/authToken';
import {
  MapPin, Upload, Users, UserCheck, Clock, Download, FileSpreadsheet,
  Search, RefreshCw, ChevronRight, Loader2, CheckCircle, XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ProvincialDashboard = ({ eventId }) => {
  const qc = useQueryClient();
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadProvince, setUploadProvince] = useState('');
  const [autoRegister, setAutoRegister] = useState(false);
  const [uploadData, setUploadData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch provinces
  const { data: provinces = [], isLoading } = useQuery({
    queryKey: ['provincial-provinces', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/provincial/provinces`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      return res.ok ? res.json() : [];
    }
  });

  // Fetch delegates for selected province
  const { data: provincialData } = useQuery({
    queryKey: ['provincial-delegates', eventId, selectedProvince],
    queryFn: async () => {
      const url = selectedProvince 
        ? `${API_URL}/api/events/${eventId}/provincial/list?province=${encodeURIComponent(selectedProvince)}`
        : `${API_URL}/api/events/${eventId}/provincial/list`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      return res.ok ? res.json() : { delegates: [], province_summary: {}, total: 0 };
    },
    enabled: !!selectedProvince || showUpload
  });

  // Parse Excel/CSV file
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: 'binary' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws);
      
      const mapped = data.map(row => ({
        first_name: row['First Name'] || row['first_name'] || row['FirstName'] || '',
        last_name: row['Last Name'] || row['last_name'] || row['LastName'] || row['Surname'] || '',
        email: row['Email'] || row['email'] || '',
        phone: row['Phone'] || row['phone'] || row['Mobile'] || row['Cell'] || '',
        gender: row['Gender'] || row['gender'] || '',
        date_of_birth: row['Date of Birth'] || row['DOB'] || row['date_of_birth'] || '',
        age_group: row['Age Group'] || row['age_group'] || '',
        district: row['District'] || row['district'] || row['Area'] || '',
        organization: row['Organization'] || row['Church'] || row['Company'] || row['organization'] || '',
        role: row['Role'] || row['Title'] || row['role'] || ''
      })).filter(r => r.first_name || r.last_name);

      setUploadData(mapped);
      toast.success(`Parsed ${mapped.length} delegates from file`);
    };
    reader.readAsBinaryString(file);
  };

  // Submit upload
  const handleUploadSubmit = async () => {
    if (!uploadProvince.trim()) {
      toast.error('Please enter a province name');
      return;
    }
    if (uploadData.length === 0) {
      toast.error('No data to upload');
      return;
    }

    setUploading(true);
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/provincial/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          province: uploadProvince.trim(),
          delegates: uploadData,
          auto_register: autoRegister
        })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.detail || 'Upload failed');
      
      setUploadResult(result);
      toast.success(`Uploaded ${result.uploaded} delegates for ${uploadProvince}`);
      qc.invalidateQueries({ queryKey: ['provincial-provinces'] });
      qc.invalidateQueries({ queryKey: ['provincial-delegates'] });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Download template
  const downloadTemplate = () => {
    const template = [
      { 'First Name': 'John', 'Last Name': 'Moyo', 'Email': 'john@example.com', 'Phone': '+263771234567', 'Gender': 'male', 'Date of Birth': '1990-05-20', 'District': 'Gweru', 'Organization': 'City Church', 'Role': 'Pastor' },
      { 'First Name': 'Mary', 'Last Name': 'Ndlovu', 'Email': '', 'Phone': '+263772345678', 'Gender': 'female', 'Date of Birth': '2005-08-10', 'District': 'Kwekwe', 'Organization': 'Youth Ministry', 'Role': 'Leader' },
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Provincial Delegates');
    XLSX.writeFile(wb, 'provincial_delegate_template.xlsx');
    toast.success('Template downloaded');
  };

  const totalDelegates = provinces.reduce((sum, p) => sum + p.total, 0);
  const totalRegistered = provinces.reduce((sum, p) => sum + p.registered, 0);

  const filteredDelegates = (provincialData?.delegates || []).filter(d => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (d.name || '').toLowerCase().includes(term) ||
           (d.email || '').toLowerCase().includes(term) ||
           (d.phone || '').includes(term) ||
           (d.district || '').toLowerCase().includes(term);
  });

  return (
    <div className="space-y-6" data-testid="provincial-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Provincial Database</h2>
          <p className="text-sm text-slate-500">{totalDelegates} delegates across {provinces.length} provinces</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-provincial-template">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Template
          </Button>
          <Button size="sm" onClick={() => { setShowUpload(true); setUploadResult(null); setUploadData([]); setUploadProvince(''); }} data-testid="upload-provincial-btn">
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload Province
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-slate-900">{provinces.length}</p>
          <p className="text-xs text-slate-500">Provinces</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-slate-900">{totalDelegates}</p>
          <p className="text-xs text-slate-500">Total Pre-Registered</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{totalRegistered}</p>
          <p className="text-xs text-slate-500">Confirmed Registered</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{totalDelegates - totalRegistered}</p>
          <p className="text-xs text-slate-500">Pending</p>
        </CardContent></Card>
      </div>

      {/* Province Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : provinces.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <MapPin className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-500">No Provincial Data Yet</h3>
            <p className="text-sm text-slate-400 mt-1">Upload an Excel sheet with delegates by province to get started</p>
            <Button className="mt-4" onClick={() => setShowUpload(true)}>
              <Upload className="h-4 w-4 mr-2" /> Upload First Province
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {provinces.map(p => {
            const pct = p.total > 0 ? Math.round((p.registered / p.total) * 100) : 0;
            return (
              <Card 
                key={p.province} 
                className={`cursor-pointer transition-all hover:shadow-md ${selectedProvince === p.province ? 'ring-2 ring-indigo-500 bg-indigo-50' : ''}`}
                onClick={() => setSelectedProvince(selectedProvince === p.province ? null : p.province)}
                data-testid={`province-card-${p.province.replace(/\s/g, '-')}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-indigo-500" />
                      <h3 className="font-semibold text-slate-900">{p.province}</h3>
                    </div>
                    <Badge variant="outline">{p.total}</Badge>
                  </div>
                  <Progress value={pct} className="h-2 mb-2" />
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1"><UserCheck className="h-3 w-3 text-emerald-500" /> {p.registered} registered</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-amber-500" /> {p.pending} pending</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Selected Province Details */}
      {selectedProvince && (
        <Card data-testid="province-detail">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-indigo-500" />
                {selectedProvince} Delegates
              </CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search delegates..."
                  className="pl-9 h-8 text-sm"
                  data-testid="province-search"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email / Phone</TableHead>
                  <TableHead>District</TableHead>
                  <TableHead>Age Group</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDelegates.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">No delegates found</TableCell></TableRow>
                ) : filteredDelegates.slice(0, 50).map((d, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium text-sm">{d.name || `${d.first_name} ${d.last_name}`}</TableCell>
                    <TableCell className="text-xs">
                      {d.email && <p className="text-slate-600">{d.email}</p>}
                      {d.phone && <p className="text-slate-400">{d.phone}</p>}
                    </TableCell>
                    <TableCell className="text-sm">{d.district || '-'}</TableCell>
                    <TableCell>
                      {d.age_group ? <Badge variant="outline" className="text-xs">{d.age_group}</Badge> : <span className="text-xs text-slate-400">-</span>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{d.organization || '-'}</TableCell>
                    <TableCell>
                      <Badge className={d.registered ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {d.registered ? 'Registered' : d.status || 'Uploaded'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filteredDelegates.length > 50 && (
              <p className="text-xs text-slate-400 text-center mt-2">Showing 50 of {filteredDelegates.length} delegates</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-indigo-500" />
              Upload Provincial Delegates
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Province Name *</Label>
              <Input
                value={uploadProvince}
                onChange={(e) => setUploadProvince(e.target.value)}
                placeholder="e.g. Midlands, Harare, Bulawayo"
                data-testid="upload-province-name"
              />
            </div>
            <div>
              <Label>Upload File (Excel/CSV)</Label>
              <Input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload} data-testid="upload-province-file" />
              <p className="text-xs text-slate-400 mt-1">Columns: First Name, Last Name, Email, Phone, Gender, Date of Birth, District, Organization, Role</p>
            </div>
            {uploadData.length > 0 && (
              <div className="p-3 bg-emerald-50 rounded-lg text-sm text-emerald-700">
                <CheckCircle className="h-4 w-4 inline mr-1" />
                {uploadData.length} delegates parsed from file
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="auto-register"
                checked={autoRegister}
                onChange={(e) => setAutoRegister(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="auto-register" className="text-sm cursor-pointer">
                Auto-register delegates immediately (creates event delegate records)
              </Label>
            </div>
            {uploadResult && (
              <div className="p-3 bg-blue-50 rounded-lg space-y-1 text-sm">
                <p className="font-medium text-blue-800">Upload Complete</p>
                <p className="text-blue-700">Uploaded: {uploadResult.uploaded} | Skipped: {uploadResult.skipped} | Auto-registered: {uploadResult.auto_registered}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button onClick={handleUploadSubmit} disabled={uploading || uploadData.length === 0 || !uploadProvince.trim()}>
              {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading...</> : <><Upload className="h-4 w-4 mr-1" /> Upload {uploadData.length} Delegates</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProvincialDashboard;
